import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseStatus } from '@core/enums/course-status';
import { getModulesCourseById, IModuleDefinition, IModulesCourseDefinition } from '../../data/course-modules.data';
import { ModuleFlowService } from '../../services/module-flow.service';
import { ModuleProgressService } from '../../services/module-progress.service';
import { AccessibleConfirmService } from '../../services/accessible-confirm.service';

interface IModuleRow {
  module: IModuleDefinition;
  isCompleted: boolean;
  status: CourseStatus;
}

@Component({
  selector: 'prism-module-list',
  templateUrl: './module-list.component.html',
  styleUrl: './module-list.component.scss'
})
export class ModuleListComponent implements OnInit {
  @ViewChild('startModuleContent', { static: true })
  startModuleContent!: TemplateRef<any>;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private confirmService = inject(AccessibleConfirmService);
  private flow = inject(ModuleFlowService);
  private progress = inject(ModuleProgressService);

  course?: IModulesCourseDefinition;
  courseId = 0;
  rows: IModuleRow[] = [];
  selectedModule?: IModuleDefinition;
  CourseStatus = CourseStatus;

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.courseId = +params['courseId'];
      this.course = getModulesCourseById(this.courseId);
      this.progress.loadCourseProgress(this.courseId).subscribe(() => {
        this.refreshRows();
      });
    });
  }

  refreshRows(): void {
    if (!this.course) {
      this.rows = [];
      return;
    }
    this.rows = this.course.modules.map(module => ({
      module,
      isCompleted: this.progress.getProgress(this.courseId, module.id).isCompleted,
      status: this.progress.getModuleStatus(this.courseId, module.id)
    }));
  }

  get completedCount(): number {
    return this.rows.filter(r => r.isCompleted).length;
  }

  buttonText(row: IModuleRow): string {
    return row.isCompleted ? 'View Module' : 'Start Module';
  }

  get selectedRequiresMinimumStay(): boolean {
    return !!this.selectedModule?.timeoutSeconds;
  }

  get selectedRequiresNotebookEntry(): boolean {
    return this.selectedModule?.requiresNotebookEntry !== false;
  }

  get selectedRequiresAcknowledgement(): boolean {
    return this.selectedModule?.requiresAcknowledgement !== false;
  }

  openModule(row: IModuleRow): void {
    if (row.isCompleted) {
      this.navigateToModule(row.module.id);
      return;
    }

    this.selectedModule = row.module;
    this.confirmService.confirm({
      nzContent: this.startModuleContent,
      nzOkText: 'OK',
      nzIconType: '',
      nzClosable: false,
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        this.navigateToModule(row.module.id);
      }
    }, `Please Note: To receive credit for ${row.module.name}`);
  }

  private navigateToModule(moduleId: string): void {
    this.progress.markModuleStarted(this.courseId, moduleId);
    this.flow.passPopup();
    window.location.href = `/course-modules/${this.courseId}/module/${moduleId}`;
  }

  formatTimeout(seconds: number | undefined): string {
    if (!seconds) return '';
    if (seconds < 60) {
      return `${seconds} second${seconds === 1 ? '' : 's'}`;
    }
    const minutes = Math.round(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  goBackToDetail(): void {
    window.location.href = `/course-modules/${this.courseId}`;
  }
}
